import React, { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Company } from "../types";
import ColorPicker from "./ColorPicker";
import FormField from "./FormField";
import FormLayout from "./FormLayout";
import Input from "./Input";
import ProfilePictureEditor from "./ProfilePictureEditor";
import { getCSRFToken } from "./utils/various";
import { FormattedMessage, useIntl } from "react-intl";
import Select from "./Select";
import LoadingUI from "./LoadingUI";

function CompanyForm({
  company: { avatar_image, logo_image, publisher_variables, ...company },
  timezones,
  availableLocales,
}: Readonly<{
  company: Company & { publisher_variables?: Record<string, string> };
  timezones: [string, string][];
  availableLocales: string[];
}>) {
  const intl = useIntl();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      company: {
        ...company,
        password: null,
        password_confirmation: null,
        publisher_variables: publisher_variables || {},
      },
    },
  });

  const defaultRequiredMessage = intl.formatMessage({ id: "company_form.required", defaultMessage: "Ce champ ne peut être vide." });

  const [loading, setLoading] = useState(false);

  const onSubmit = async (data: any) => {
    setLoading(true);
    console.log(data);
    if (avatarImage) {
      // On renvoie avatarImage sans la picture (mais avec id si on en a reçu une)
      const { serialized_file, ...rest } = avatarImage;
      data.company.avatar_image_attributes = rest;
      if ("signature" in serialized_file) {
        // Si nouvelle image, on renvoie la nouvelle url sous la forme identifier#signature
        data.company.avatar_image_attributes.picture =
          serialized_file.identifier + "#" + serialized_file.signature;
      }
    }

    if (logoImage) {
      // On renvoie logoImage sans la picture (mais avec id si on en a reçu une)
      const { serialized_file, ...rest } = logoImage;
      data.company.logo_image_attributes = rest;
      console.log(data.company.logo_image_attributes);
      if ("signature" in serialized_file) {
        // Si nouvelle image, on renvoie la nouvelle url sous la forme identifier#signature
        data.company.logo_image_attributes.picture =
          serialized_file.identifier + "#" + serialized_file.signature;
      }
    }
    const authenticity_token = getCSRFToken();
    const response = await fetch(`/companies/${company.id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": authenticity_token,
        "Content-Type": "application/json",
      },
    });
    setLoading(false);
    if (response.ok) {
      window.location.reload();
    }
  };

  // Variable management functions
  const addVariable = () => {
    if (!newVariableKey.trim() || !newVariableValue.trim()) return;

    const key = newVariableKey.toUpperCase().replace(/^\$/, "");
    const currentVariables = watchedPublisherVariables || {};
    const updatedVariables = { ...currentVariables, [key]: newVariableValue };

    setValue("company.publisher_variables", updatedVariables);
    setNewVariableKey("");
    setNewVariableValue("");
  };

  const removeVariable = (keyToRemove: string) => {
    const currentVariables = watchedPublisherVariables || {};
    const updatedVariables = { ...currentVariables };
    delete updatedVariables[keyToRemove];
    setValue("company.publisher_variables", updatedVariables);
  };

  const genderOptions = [
    { label: intl.formatMessage({ id: "company_form.gender_company", defaultMessage: "Je suis une entreprise" }), value: "Je suis une entreprise" },
    { label: intl.formatMessage({ id: "company_form.gender_independent_f", defaultMessage: "Je suis une indépendante" }), value: "Je suis une indépendante" },
    { label: intl.formatMessage({ id: "company_form.gender_independent_m", defaultMessage: "Je suis un indépendant" }), value: "Je suis un indépendant" },
    { label: intl.formatMessage({ id: "company_form.gender_health_m", defaultMessage: "Je suis un professionnel de santé" }), value: "Je suis un professionnel de santé" },
    { label: intl.formatMessage({ id: "company_form.gender_health_f", defaultMessage: "Je suis une professionnelle de santé" }), value: "Je suis une professionnelle de santé" },
    { label: intl.formatMessage({ id: "company_form.gender_law_firm", defaultMessage: "Je suis un cabinet d'avocat" }), value: "Je suis un cabinet d'avocat" },
  ];

  const timezoneOptions = timezones.map((t) => ({ label: t[0], value: t[1] }));

  const localeOptions = [
    { label: "🇫🇷 Français", value: "fr" },
    { label: "🇬🇧 English", value: "en" },
    { label: "🇪🇸 Español", value: "es" },
    { label: "🇵🇹 Português", value: "pt" },
  ];

  const [avatarImage, setAvatarImage] = useState(avatar_image);
  const [logoImage, setLogoImage] = useState(logo_image);

  // Variable management state
  const [newVariableKey, setNewVariableKey] = useState("");
  const [newVariableValue, setNewVariableValue] = useState("");

  const watchedPublisherVariables = watch("company.publisher_variables");

  const inputDisabled = useCallback(
    (inputName: string) => company.disabled_company_fields?.includes(inputName),
    [company.disabled_company_fields]
  );

  const inputHidden = useCallback(
    (inputName: string) => company.hidden_company_fields?.includes(inputName),
    [company.hidden_company_fields]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="">
      <FormLayout
        title={intl.formatMessage({
          id: "company_form.my_company",
          defaultMessage: "Mon entreprise",
        })}
        leftSideElements={
          <>
            <div className="relative w-full rounded-md border p-4">
              <p className="w-full text-center block mb-1 text-base font-semibold text-gray-700">
                {intl.formatMessage({ id: "company_form.my_photo", defaultMessage: "Ma photo" })}
              </p>
              <ProfilePictureEditor
                profilePicture={avatarImage}
                setProfilePicture={setAvatarImage}
              />
              <p className="w-full text-center mt-2 text-sm text-gray-500">
                {intl.formatMessage({
                  id: "company_form.photo_hint",
                  defaultMessage:
                    "Le nom à travers lequel vous apparaîtrez dans les messages envoyés",
                })}
              </p>
            </div>
            <div className="relative w-full mt-4 rounded-md border p-4">
              <p className="w-full text-center block mb-1 text-base font-semibold text-gray-700">
                {intl.formatMessage({ id: "company_form.my_logo", defaultMessage: "Mon logo" })}
              </p>
              <ProfilePictureEditor
                profilePicture={logoImage}
                setProfilePicture={setLogoImage}
                notRounded={true}
                disabled={inputDisabled("logo")}
              />
            </div>
          </>
        }
        description={intl.formatMessage({
          id: "company_form.edit",
          defaultMessage: "Éditez les informations vous concernant",
        })}
      >
        <Input
          title={intl.formatMessage({
            id: "company_form.displayed_name",
            defaultMessage: "Enseigne",
          })}
          {...register("company.displayed_name", {
            required: defaultRequiredMessage,
          })}
          error={errors?.company?.company?.message}
          disabled={inputDisabled("displayed_name")}
          readonly={inputDisabled("displayed_name")}
        />

        <Input
          title={intl.formatMessage({
            id: "company_form.company_name",
            defaultMessage: "Groupe",
          })}
          hint={intl.formatMessage({
            id: "company_form.company_name_hint",
            defaultMessage: "Le nom de votre groupe ou marque",
          })}
          {...register("company.company", { required: defaultRequiredMessage })}
          error={errors?.company?.company?.message}
          disabled={inputDisabled("company")}
          readonly={inputDisabled("company")}
        />

        <Input
          title={intl.formatMessage({
            id: "company_form.email",
            defaultMessage: "Email",
          })}
          {...register("company.email", { required: defaultRequiredMessage })}
          error={errors?.company?.email?.message}
          disabled={inputDisabled("email")}
          readonly={inputDisabled("email")}
        />
        <Input
          title={intl.formatMessage({
            id: "company_form.phone",
            defaultMessage: "Téléphone",
          })}
          {...register("company.phone", { required: defaultRequiredMessage })}
          error={errors?.company?.phone?.message}
          disabled={inputDisabled("phone")}
          readonly={inputDisabled("phone")}
        />
        <Input
          title={intl.formatMessage({
            id: "company_form.website",
            defaultMessage: "Site web",
          })}
          {...register("company.website")}
          hint={intl.formatMessage({
            id: "company_form.website_hint",
            defaultMessage: "Votre site web",
          })}
          disabled={inputDisabled("website")}
          readonly={inputDisabled("website")}
        />

        {!inputHidden("gender") && (
          <FormField
            title={intl.formatMessage({
              id: "company_form.gender",
              defaultMessage: "Genre",
            })}
            hint={intl.formatMessage({
              id: "company_form.gender_hint",
              defaultMessage:
                "Personnalisez votre message au féminin ou masculin",
            })}
          >
            <Controller
              name="company.gender"
              control={control}
              render={({ field }) => (
                <Select
                  onChange={(option) => field.onChange(option)}
                  list={genderOptions}
                  value={field.value}
                />
              )}
            />
          </FormField>
        )}
        {!inputHidden("color") && (
          <FormField
            title={intl.formatMessage({
              id: "company_form.color",
              defaultMessage: "Couleur",
            })}
            hint={intl.formatMessage({
              id: "company_form.color_hint",
              defaultMessage: "Utilisée pour décorer vos contenus",
            })}
            disabled={inputDisabled("color")}
            readonly={inputDisabled("color")}
          >
            <Controller
              name="company.primary_color"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <ColorPicker
                  name={name}
                  onChange={onChange}
                  value={value}
                  error={errors?.company?.primary_color?.message}
                />
              )}
            />
          </FormField>
        )}
        <Input
          title={intl.formatMessage({
            id: "company_form.hashtags",
            defaultMessage: "Hashtags",
          })}
          hint={intl.formatMessage({
            id: "company_form.hashtags_hint",
            defaultMessage: "La liste de vos hashtags commençant par '#'",
          })}
          {...register("company.hashtag", {
            required: false,
            pattern: /^[#]\w+/,
          })}
          error={errors?.company?.hashtag?.message}
          disabled={inputDisabled("hashtags")}
          readonly={inputDisabled("hashtags")}
        />
        {!inputHidden("timezone") && (
          <FormField
            title={intl.formatMessage({
              id: "company_form.timezone",
              defaultMessage: "Fuseau horaire",
            })}
          >
            <Controller
              name="company.timezone"
              control={control}
              render={({ field }) => (
                <Select
                  onChange={(option) => field.onChange(option)}
                  list={timezoneOptions}
                  value={field.value}
                />
              )}
            />
          </FormField>
        )}

        <FormField
          title={intl.formatMessage({
            id: "company_form.locale",
            defaultMessage: "Langue",
          })}
          hint={intl.formatMessage({
            id: "company_form.locale_hint",
            defaultMessage: "Langue notamment utilisée pour les emails automatiques de cette enseigne",
          })}
        >
          <Controller
            name="company.locale"
            control={control}
            render={({ field }) => (
              <Select
                onChange={(option) => field.onChange(option)}
                list={localeOptions}
                value={field.value}
              />
            )}
          />
        </FormField>

        {/* Custom Variables Section */}
        <FormField
          title={intl.formatMessage({
            id: "company_form.custom_variables",
            defaultMessage: "Variables personnalisées",
          })}
          hint={intl.formatMessage({
            id: "company_form.custom_variables_hint",
            defaultMessage:
              "Créez vos propres variables pour personnaliser vos publications",
          })}
        >
          <div className="space-y-6">
            {/* Save reminder - always visible */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-xs text-blue-700">
                {intl.formatMessage({ id: "company_form.save_reminder", defaultMessage: "💡 N'oubliez pas d'enregistrer le formulaire pour valider vos modifications" })}
              </p>
            </div>

            {/* Existing Variables */}
            {Object.keys(watchedPublisherVariables || {}).length > 0 && (
              <div className="space-y-3">
                <div className="space-y-2">
                  {Object.entries(watchedPublisherVariables || {}).map(
                    ([key, value]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-md border border-gray-200"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <span className="inline-flex items-center p-2 rounded-md text-xs font-medium bg-brand_lighter text-brand_darker">
                              ${key}
                            </span>
                            <span className="text-sm text-gray-700 truncate">
                              {value}
                            </span>
                          </div>
                        </div>
                        {!inputDisabled(key) && (
                          <button
                            type="button"
                            onClick={() => removeVariable(key)}
                            className="ml-3 inline-flex items-center p-1.5 border border-transparent text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 rounded-md"
                          >
                            <svg
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Add New Variable */}
            {!inputDisabled("publisher_variables") && (
              <div className="border-t pt-4">
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-3">
                    <input
                      type="text"
                      placeholder={intl.formatMessage({ id: "company_form.variable_name", defaultMessage: "MA_VARIABLE" })}
                      value={newVariableKey}
                      onChange={(e) => {
                        const cleaned = e.target.value
                          .toUpperCase()
                          .replace(/\s+/g, "_")
                          .replace(/[^A-Z0-9_]/g, "");
                        setNewVariableKey(cleaned);
                      }}
                      onKeyPress={(e) =>
                        e.key === "Enter" && (e.preventDefault(), addVariable())
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-7">
                    <input
                      type="text"
                      placeholder={intl.formatMessage({ id: "company_form.variable_value", defaultMessage: "Valeur de la variable" })}
                      value={newVariableValue}
                      onChange={(e) => setNewVariableValue(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && (e.preventDefault(), addVariable())
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <button
                      type="button"
                      onClick={addVariable}
                      disabled={
                        !newVariableKey.trim() || !newVariableValue.trim()
                      }
                      className="btn-brand-secondary disabled:border-gray-400 disabled:text-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed"
                    >
                      {intl.formatMessage({ id: "company_form.add", defaultMessage: "Ajouter" })}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </FormField>

        <div className="flex justify-end">
          <button
            className={`btn-brand-primary ${
              loading ? "!bg-gray-500 cursor-default" : ""
            } space-x-3`}
            type="submit"
            disabled={loading}
          >
            <FormattedMessage
              id="company_form.save"
              defaultMessage="Enregistrer"
            />

            {loading && (
              <div className="ml-2">
                <LoadingUI />
              </div>
            )}
          </button>
        </div>
      </FormLayout>
    </form>
  );
}

export default CompanyForm;
